/**
 * `changeSharingWire.ts` — the `change_sharing` (tc 48) and `evict_user`
 * (tc 47) envelopes, ported from `Models/EventModel/EventData/
 * ChangeSharingEventData.cs` and `EvictUserEventData.cs`.
 *
 * ⚠ THREE THINGS ON THIS WIRE WILL BITE A PORTER.
 *
 * 1. **ONE int field backs TWO typed properties.** `ChangeSharingEventData`
 *    serializes a single `content` (`:41-54`) which the C# exposes as BOTH
 *    `UserRole` and `PlanType`. A receiver must switch on `shareEvt` to know
 *    which it is holding; a DTO with two separate fields would be wrong on
 *    the wire.
 *
 * 2. **`AccountRoleChanged` carries `userId = ""`** (`ShareViewModel.cs:750`)
 *    — it addresses the whole ACCOUNT, not a person. Any receiver filtering
 *    on `userId === me` drops it entirely, which is why upstream handles it
 *    in a separate branch from the three person-addressed events.
 *
 * 3. **Both types are on Unity's NEVER-IGNORE whitelist**
 *    (`AWSServerEventListenerModel.cs:215-216`), so OWN ECHOES ARE DELIVERED
 *    and four of the five upstream consumers process them deliberately (to
 *    refresh the sender's own grid and share sheet). Only
 *    `PermissionsModel.OnServerSharingChanged` carries a sender guard
 *    (`:232-233`). A blanket own-echo drop in the reconciliation layer would
 *    silently break the sender's own refresh — which is exactly why these two
 *    types are routed BEFORE that drop.
 */

/** `ChangeSharingEventType` (`ChangeSharingEventData.cs:56-63`). Delete is NOT last. */
export const CHANGE_SHARING_EVENT = {
  ProjectShared: 0,
  ProjectUnshared: 1,
  /** The role on an individual shared LINK changed. */
  UserRoleChanged: 2,
  /** The account-wide sharing role changed. Addresses everyone; `userId` is "". */
  AccountRoleChanged: 3,
  /** A re-broadcast the RECEIVER emits so peers update its roster row. */
  ParticipantUpdate: 4,
} as const;

export interface ChangeSharingEvent {
  /** The SUBJECT of the change — empty for `AccountRoleChanged`. */
  readonly userId: string;
  readonly projectId: string;
  readonly accountId: string;
  readonly shareEvent: number;
  /** The single `content` int, read as a `SharedUserRole` or a plan type. */
  readonly content: number;
  /** `aid` — the originating APP INSTANCE. */
  readonly originatingAppInstanceId: string;
  /**
   * `uid` — the originating USER (`BaseEventData.cs:56-62`). The permissions
   * leg guards on THIS, not on `aid`, so a change this user made in another
   * tab is skipped too (Unity `PermissionsModel.cs:232-233`).
   */
  readonly originatingUserId: string;
}

export interface EvictUserEvent {
  readonly userId: string;
  readonly projectId: string;
}

export function parseChangeSharingEvent(
  payload: Record<string, unknown>,
): ChangeSharingEvent | null {
  const shareEvent = num(payload, "shareEvt", "ShareEvent", "shareEvent");
  if (shareEvent === null) return null;
  return {
    userId: str(payload, "userId", "UserId", "user_id"),
    // Unity's serialized names are the SHORT ones (`projId`, `actId`).
    projectId: str(payload, "projId", "projectId", "ProjectId", "project_id"),
    accountId: str(payload, "actId", "accountId", "AccountId"),
    shareEvent,
    content: num(payload, "content", "Content") ?? 0,
    originatingAppInstanceId: str(payload, "aid", "OriginatingAppInstanceId"),
    originatingUserId: str(payload, "uid", "OriginatingUserId"),
  };
}

export function parseEvictUserEvent(
  payload: Record<string, unknown>,
): EvictUserEvent | null {
  const userId = str(payload, "userId", "UserId", "user_id");
  if (!userId) return null;
  return {
    userId,
    projectId: str(payload, "projectId", "ProjectId", "projId", "project_id"),
  };
}

/**
 * The OUTBOUND `change_sharing` payload — `AWSServerEventSenderModel.
 * SendChangeSharingEvent` (`:460-474`) builds a `ChangeSharingEventData` whose
 * serialized names are the SHORT ones (`projId`, `actId`, `shareEvt`,
 * `content`). `actId` is the TARGET account: the sessions server fans the
 * event out to that account's connections, which is the whole cross-account
 * mechanism — there is no second socket and no envelope override.
 * `userId` is the SUBJECT (the sharee), `""` for `AccountRoleChanged`.
 */
export function buildChangeSharingPayload(args: {
  readonly accountId: string;
  readonly projectId: string;
  readonly userId: string;
  readonly shareEvent: number;
  readonly content: number;
}): Record<string, unknown> {
  return {
    userId: args.userId,
    projId: args.projectId,
    actId: args.accountId,
    shareEvt: args.shareEvent,
    content: args.content,
  };
}

/** The OUTBOUND `evict_user` payload — `SendEvictUserEvent` (`:447-457`). */
export function buildEvictUserPayload(args: {
  readonly projectId: string;
  readonly userId: string;
}): Record<string, unknown> {
  return {
    projectId: args.projectId,
    userId: args.userId,
  };
}

/** Does this event change which projects a person can see? */
export function changesProjectList(shareEvent: number): boolean {
  return shareEvent === CHANGE_SHARING_EVENT.ProjectShared
    || shareEvent === CHANGE_SHARING_EVENT.ProjectUnshared
    || shareEvent === CHANGE_SHARING_EVENT.UserRoleChanged;
}

function str(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return "";
}

function num(record: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}
